<template>
  <ModalComponent
    :header="true"
    :close-header-button="true"
    :custom-footer="true"
    max-width="400px"
    width="90%"
    @close="$emit('close')"
  >
    <template #header>
      <h3 class="modal-title">Add to Breakout Room</h3>
    </template>

    <div class="breakout-modal-body">
      <p class="modal-description">
        Select a breakout room to move <strong>{{ participantName }}</strong> into:
      </p>

      <ul class="room-list">
        <li
          v-for="(room, index) in rooms"
          :key="room.roomName"
          class="room-item"
          :class="{
            selected: selectedRoom === room.roomName,
            current: isCurrentRoom(room.roomName)
          }"
          @click="!isCurrentRoom(room.roomName) && (selectedRoom = room.roomName)"
        >
          <span class="radio-circle">
            <span v-if="selectedRoom === room.roomName" class="radio-dot"></span>
          </span>
          <div class="room-info">
              <span class="room-label">Room {{ index + 1 }}</span>
            <span class="room-participants">{{ getParticipantCount(room) }} participant(s)</span>
          </div>
        </li>
      </ul>

      <div v-if="rooms.length === 0" class="no-rooms">
        No active breakout rooms available.
      </div>
    </div>

    <template #footer>
      <div class="breakout-modal-footer">
        <PgeButton
          template="text"
          label="Cancel"
          button-size="medium"
          @click="$emit('close')"
        />
        <PgeButton
          template="filled"
          label="Move"
          button-size="medium"
          :disabled="!selectedRoom || isCurrentRoom(selectedRoom)"
          @click="handleMove"
        />
      </div>
    </template>
  </ModalComponent>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import ModalComponent from './shared/modals/ModalComponent.vue';
import PgeButton from '../../pge-components/pge-button.vue';

const props = defineProps({
  participantIdentity: {
    type: String,
    required: true
  },
  participantName: {
    type: String,
    required: true
  },
  rooms: {
    type: Array,
    required: true
    // [{roomName, participantIdentities: []}]
  }
});

const emit = defineEmits(['close', 'move']);

const selectedRoom = ref(null);
const liveParticipantCounts = ref({}); // { roomName: count }
const isLoadingCounts = ref(false);

const fetchLiveCounts = async () => {
  isLoadingCounts.value = true;

  for (const room of props.rooms) {
    try {
      const response = await axios.get(
        `/api/live/rooms/${encodeURIComponent(room.roomName)}/participants`
      );
      liveParticipantCounts.value[room.roomName] = response.data?.data?.count ?? room.participantIdentities.length;
    } catch (e) {
      // Fallback to stored count if API fails
      liveParticipantCounts.value[room.roomName] = room.participantIdentities.length;
    }
  }
  isLoadingCounts.value = false;
};

const getParticipantCount = (room) => {
  if (liveParticipantCounts.value[room.roomName] !== undefined) {
    return liveParticipantCounts.value[room.roomName];
  }
  return room.participantIdentities?.length || 0;
};

const isCurrentRoom = (roomName) => {
  const room = props.rooms.find(r => r.roomName === roomName);
  return room?.participantIdentities?.includes(props.participantIdentity);
};

onMounted(() => {
  fetchLiveCounts();
  // Preselect current room
  const currentRoom = props.rooms.find(r => r.participantIdentities?.includes(props.participantIdentity));
  if (currentRoom) {
     selectedRoom.value = currentRoom.roomName;
  }
});

const handleMove = () => {
  if (!selectedRoom.value) return;
  emit('move', {
    participantIdentity: props.participantIdentity,
    participantName: props.participantName,
    targetRoomName: selectedRoom.value
  });
};
</script>

<style scoped>
.modal-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.breakout-modal-body {
  padding: 16px 20px;
}

.modal-description {
  font-size: 14px;
  color: #4b5563;
  margin: 0 0 16px 0;
  line-height: 1.5;
}

.room-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.room-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.room-item.current {
  background: #fdfafb;
  border-color: #e5e7eb;
  cursor: default;
  opacity: 0.8;
}

.room-item:not(.current):hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.room-item.selected:not(.current) {
  background: #f3e8ff;
  border-color: #7c3aed;
}

.room-item.selected.current {
  border-color: #e5e7eb;
  background: #f9fafb;
}

.room-item.selected.current .radio-circle {
  border-color: #d1d5db;
}

.room-item.selected.current .radio-dot {
  background: #d1d5db;
}

.radio-circle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.15s;
}

.room-item.selected .radio-circle {
  border-color: #7c3aed;
}

.radio-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #7c3aed;
}

.room-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.room-label {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
}

.room-participants {
  font-size: 12px;
  color: #6b7280;
}

.no-rooms {
  text-align: center;
  color: #6b7280;
  font-size: 14px;
  padding: 24px 0;
}

.breakout-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
}
</style>
